import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import BookingModal from '../components/BookingModal';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../lib/supabaseClient';
import { defaultContent } from '../lib/defaultContent';
import { useRegisterEditor } from '../lib/EditorContext';

const { FiCalendar, FiClock, FiVideo } = FiIcons;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const defaultWorkshops = [
  {
    id: 1,
    title: "The Unraveled Parent",
    date: "May 8, 2026",
    type: "Live Event",
    price: 49,
    desc: "A comprehensive deep dive into maintaining your sanity and emotional regulation, exploring daily logs, weekly taunt tracking, and surviving the chaos of parenthood.",
    instructor: "Beth Black LPC, ATR-BC, CPCS and Kelly Carlisle LAPC",
    image: "/The_Unraveled_Parent_Workshop.png",
    soldOut: true
  },
  {
    id: 2,
    title: "The Teen Connection Blueprint",
    date: "Self-Paced Course",
    time: "4 Hours of Content",
    type: "On-Demand Video",
    price: 129,
    desc: "Rebuild trust and open communication channels with your teenager. This comprehensive course covers boundaries, screen time, and emotional regulation.",
    instructor: "Dr. Mark Lewis",
    image: "https://images.unsplash.com/photo-1577896851231-70ef18881754?auto=format&fit=crop&q=80",
    locked: true
  },
  {
    id: 3,
    title: "Co-Parenting Harmony Workshop",
    date: "Next Session: Nov 02, 2023",
    time: "1:00 PM - 4:00 PM PST",
    type: "Live Virtual Event",
    price: 89,
    desc: "For couples looking to get on the same page. Address differing parenting styles and learn how to present a united, supportive front for your children.",
    instructor: "Dr. Jenkins & Dr. Lewis",
    image: "https://images.unsplash.com/photo-1516627145497-ae6968895b74?auto=format&fit=crop&q=80",
    locked: true
  }
];

export default function Workshops() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchContent() {
      try {
        const { data, error } = await supabase
          .from('page_content')
          .select('content')
          .eq('page_id', 'workshops')
          .single();

        if (error) {
          console.warn('Could not fetch workshops content, using defaults.', error.message);
        } else if (data && data.content) {
          setContent(data.content);
        }
      } catch (err) {
        console.error('Error fetching workshops content:', err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchContent();
  }, []);

  const handleBook = (item) => {
    if (item.soldOut || item.locked) return;
    setSelectedItem(item);
    setModalOpen(true);
  };

  const title = content?.title || defaultContent.workshops.title;
  const subtitle = content?.subtitle || defaultContent.workshops.subtitle;
  const currentWorkshops = content?.workshops || defaultContent.workshops.workshops;

  const handleSaveSuccess = (newContent) => {
    setContent(newContent);
  };

  useRegisterEditor(
    'workshops',
    { title, subtitle, workshops: currentWorkshops },
    handleSaveSuccess,
    'Workshops Page'
  );

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <motion.div initial="hidden" animate="visible" exit="hidden" className="py-12 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl md:text-5xl  font-bold text-[#333] dark:text-[#fff] mb-6">{title}</h1>
          <p className="text-lg text-[#333] dark:text-[#fff]">
            {subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {currentWorkshops.map((item) => (
            <motion.div key={item.id} variants={fadeUp} className={`bg-white dark:bg-brand-slate rounded-2xl overflow-hidden shadow-sm border border-sand-100 flex flex-col hover:shadow-md transition-shadow relative`}>
              {(item.locked || item.completed) && (
                <div className="absolute inset-0 bg-white/80 dark:bg-brand-slate/80 backdrop-blur-sm z-20 flex flex-col items-center justify-center p-6 text-center">
                  <div className="text-6xl mb-4">❤️🔒</div>
                  <h3 className="text-2xl font-bold text-[#333] dark:text-[#fff] mb-2">
                    {item.completed ? 'Workshop Completed' : 'Locked Content'}
                  </h3>
                  <p className="text-[#333] dark:text-[#fff] mb-6">
                    {item.completed ? 'This workshop has already taken place.' : 'This workshop is currently unavailable.'}
                  </p>
                  <button className="px-8 py-3 bg-gray-300 text-gray-700 rounded-full font-medium cursor-not-allowed">
                    {item.completed ? 'Completed' : 'Coming Soon!'}
                  </button>
                </div>
              )}

              <div className="h-48 overflow-hidden relative">
                <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                <div className="absolute top-4 right-4 bg-white/90 dark:bg-brand-slate/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-[#333] dark:text-[#fff]">
                  ${item.price}
                </div>
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <div className="flex items-center gap-2 text-xs font-medium text-[#333] dark:text-[#fff] mb-3 uppercase tracking-wider">
                  <SafeIcon icon={item.type.includes('Live') ? FiCalendar : FiVideo} />
                  {item.type}
                </div>
                <h3 className="text-xl  font-bold text-[#333] dark:text-[#fff] mb-2">{item.title}</h3>
                <p className="text-sm text-[#333] dark:text-[#fff] mb-4 flex-grow">{item.desc}</p>
                
                <div className="space-y-2 mb-6 text-sm text-[#333] dark:text-[#fff] border-t border-sand-100 pt-4">
                  <div className="flex items-center gap-2"><SafeIcon icon={FiCalendar} /> {item.date}</div>
                  {item.time && <div className="flex items-center gap-2"><SafeIcon icon={FiClock} /> {item.time}</div>}
                  <div className="text-xs pt-2 font-medium text-[#333] dark:text-[#fff]">Led by: {item.instructor}</div>
                </div>

                {item.soldOut ? (
                  <button disabled className="w-full px-10 py-4 bg-gray-300 text-gray-700 rounded-full font-medium text-center shadow-[2px_2px_0px_0px_rgba(45,55,72,1)] border-2 border-brand-darkGray flex items-center justify-center gap-2 cursor-not-allowed opacity-70">
                    Workshop is Full
                  </button>
                ) : (
                  <button
                    onClick={() => handleBook(item)}
                    className="w-full px-10 py-4 bg-yellow-300 text-black dark:text-black rounded-full font-medium text-center hover:brightness-110 transition-all shadow-[2px_2px_0px_0px_rgba(45,55,72,1)] border-2 border-brand-darkGray hover:translate-y-1 hover:shadow-none flex items-center justify-center gap-2"
                  >
                    {item.type.includes('Course') ? 'Enroll Now' : 'Book Seat'}
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {selectedItem && (
        <BookingModal 
          isOpen={modalOpen} 
          onClose={() => setModalOpen(false)} 
          title={selectedItem.title} 
          price={selectedItem.price} 
        />
      )}
    </motion.div>
  );
}